'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Blocks,
  ArrowLeftRight,
  Wallet,
  ShieldCheck,
  Activity,
  BarChart3,
  Home,
  Network,
  Server,
  Layers,
  Plug,
  ClipboardCheck,
  BookOpen,
} from 'lucide-react';

const navItems = [
  { href: '/explorer', label: 'Home', icon: Home, exact: true },
  { href: '/explorer/blocks', label: 'Blocks', icon: Blocks, exact: false },
  { href: '/explorer/transactions', label: 'Transactions', icon: ArrowLeftRight, exact: false },
  { href: '/explorer/addresses', label: 'Addresses', icon: Wallet, exact: false },
  { href: '/explorer/validators', label: 'Validators', icon: ShieldCheck, exact: false },
  { href: '/explorer/network', label: 'Network', icon: Network, exact: false },
  { href: '/explorer/health', label: 'Health', icon: Activity, exact: false },
  { href: '/explorer/runtime', label: 'Runtime', icon: Activity, exact: false },
  {
    href: '/explorer/runtime-integration',
    label: 'Runtime Integration',
    icon: Network,
    exact: false,
  },
  { href: '/explorer/backend-readiness', label: 'Backend Readiness', icon: Server, exact: false },
  {
    href: '/explorer/runtime-orchestration',
    label: 'Runtime Orchestration',
    icon: Layers,
    exact: false,
  },
  {
    href: '/explorer/internal-backend-integration',
    label: 'Internal Backend Integration',
    icon: Plug,
    exact: false,
  },
  {
    href: '/explorer/architecture-integration-review',
    label: 'Architecture Review',
    icon: Layers,
    exact: false,
  },
  {
    href: '/explorer/internal-execution',
    label: 'Internal Execution',
    icon: Activity,
    exact: false,
  },
  {
    href: '/explorer/storage-provider',
    label: 'Storage Provider',
    icon: Server,
    exact: false,
  },
  {
    href: '/explorer/transaction-lifecycle',
    label: 'Transaction Lifecycle',
    icon: ArrowLeftRight,
    exact: false,
  },
  {
    href: '/explorer/real-state-management',
    label: 'Real State Management',
    icon: Layers,
    exact: false,
  },
  {
    href: '/explorer/internal-transaction-flow',
    label: 'Internal Transaction Flow',
    icon: ArrowLeftRight,
    exact: false,
  },
  {
    href: '/explorer/execution-readiness-review',
    label: 'Execution Readiness Review',
    icon: ClipboardCheck,
    exact: false,
  },
  {
    href: '/explorer/internal-genesis',
    label: 'Internal Genesis',
    icon: Layers,
    exact: false,
  },
  {
    href: '/explorer/internal-validator',
    label: 'Internal Validator',
    icon: ShieldCheck,
    exact: false,
  },
  {
    href: '/explorer/internal-dry-run-transaction',
    label: 'Internal Dry-Run Transaction',
    icon: ArrowLeftRight,
    exact: false,
  },
  {
    href: '/explorer/first-internal-transaction-plan',
    label: 'First Internal Transaction Plan',
    icon: ClipboardCheck,
    exact: false,
  },
  {
    href: '/explorer/pre-execution-gate-review',
    label: 'Pre-Execution Gate Review',
    icon: ShieldCheck,
    exact: false,
  },
  {
    href: '/explorer/internal-transaction',
    label: 'Internal Transaction Execution',
    icon: ArrowLeftRight,
    exact: false,
  },
  {
    href: '/explorer/execution-authorization',
    label: 'Execution Authorization',
    icon: ShieldCheck,
    exact: false,
  },
  {
    href: '/explorer/first-controlled-internal-transaction',
    label: 'First Controlled Internal Transaction',
    icon: ArrowLeftRight,
    exact: false,
  },
  {
    href: '/explorer/activation-readiness-review',
    label: 'Activation Readiness Review',
    icon: ClipboardCheck,
    exact: false,
  },
  {
    href: '/explorer/controlled-execution-runbook',
    label: 'Controlled Execution Runbook',
    icon: BookOpen,
    exact: false,
  },
  {
    href: '/explorer/final-live-execution-checklist',
    label: 'Final Live Execution Checklist',
    icon: ClipboardCheck,
    exact: false,
  },
  {
    href: '/explorer/first-internal-transaction-result',
    label: 'First Internal Transaction Result',
    icon: ArrowLeftRight,
    exact: false,
  },
  {
    href: '/explorer/go-no-go-execution-review',
    label: 'Go / No-Go Review',
    icon: ClipboardCheck,
    exact: false,
  },
  {
    href: '/explorer/execution-implementation-review',
    label: 'Execution Implementation Review',
    icon: ClipboardCheck,
    exact: false,
  },
  {
    href: '/explorer/internal-write-path-readiness',
    label: 'Internal Write Path Readiness',
    icon: ClipboardCheck,
    exact: false,
  },
  {
    href: '/explorer/execution-verification',
    label: 'Execution Verification',
    icon: ClipboardCheck,
    exact: false,
  },
  { href: '/explorer/observability', label: 'Observability', icon: BarChart3, exact: false },
];

export default function ExplorerNav() {
  const pathname = usePathname();

  return (
    <nav data-component-id="src/app/explorer/components/ExplorerNav.tsx:195:4" data-component-path="src/app/explorer/components/ExplorerNav.tsx" data-component-line="195" data-component-end-line="217" data-component-file="ExplorerNav.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22rounded-xl%20border%20border-border%20bg-card%20p-2%22%7D" className="rounded-xl border border-border bg-card p-2">
      <ul data-component-id="src/app/explorer/components/ExplorerNav.tsx:196:6" data-component-path="src/app/explorer/components/ExplorerNav.tsx" data-component-line="196" data-component-end-line="216" data-component-file="ExplorerNav.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-1%22%7D" className="flex flex-wrap gap-1">
        {navItems.map((item) => {
          const active = item.exact ? pathname === item.href : pathname.startsWith(item.href);
          const Icon = item.icon;
          return (
            <li data-component-id="src/app/explorer/components/ExplorerNav.tsx:201:12" data-component-path="src/app/explorer/components/ExplorerNav.tsx" data-component-line="201" data-component-end-line="213" data-component-file="ExplorerNav.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%7D" key={item.href}>
              <Link data-component-id="src/app/explorer/components/ExplorerNav.tsx:202:14" data-component-path="src/app/explorer/components/ExplorerNav.tsx" data-component-line="202" data-component-end-line="212" data-component-file="ExplorerNav.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                href={item.href}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                  active
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground'
                }`}
              >
                <Icon data-component-id="src/app/explorer/components/ExplorerNav.tsx:210:16" data-component-path="src/app/explorer/components/ExplorerNav.tsx" data-component-line="210" data-component-end-line="210" data-component-file="ExplorerNav.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" size={14} />
                {item.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
